import { type FC, memo, Suspense, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { isBoolean, isNil, isObject, noop } from 'lodash';
// Import Types
import type { CJVisualizationArgumentValue } from 'types';
// Import UI Components
import { BusyLoader, Button } from '@geneui/components';
// Import Components
import SectionWithTitle from 'components/common/SectionWithTitle';
import CJCascadeDropdown from 'components/CustomerJourney/CJCascadeDropdown';
import { formulaToReferenceMapToUIModel } from 'components/CustomerJourney/config';
// Import Helpers
import {
    isCJReferenceInArgumentValue,
    type MapPropertiesToCascadeDropdownModelResult,
} from 'services/customerJourney/helpers';
// Import Constants
import { l, CJArgumentValuesTypes } from 'constants/common';
// Import Styles
import styles from './customerJourneyVisualizationReferenceValue.module.scss';

export interface CJVisualizationReferenceValueUpdate {
    value: Nullable<CJVisualizationArgumentValue>;
    isValid: boolean;
}

interface Props {
    label: string;
    formConfig: AnyObject;
    option: AnyObject;
    value?: Nullable<CJVisualizationArgumentValue>;
    referenceProperties: MapPropertiesToCascadeDropdownModelResult[];
    node: AnyObject;
    elements?: Nullable<AnyObject[]>;
    isReferenceOnly?: boolean;
    contentClassName?: string;
    hasSection?: boolean;
    getUpdate?: (data: CJVisualizationReferenceValueUpdate) => void;
}

const CustomerJourneyVisualizationReferenceValue: FC<Props> = (props) => {
    const {
        label,
        formConfig,
        option,
        value: defaultValue = null,
        referenceProperties,
        node,
        elements = null,
        isReferenceOnly = false,
        contentClassName,
        hasSection = true,
        getUpdate = noop,
    } = props;

    const { t } = useTranslation();

    const [value, setValue] = useState<Nullable<CJVisualizationArgumentValue>>(defaultValue);
    const [isFormValid, setIsFormValid] = useState<Nullable<boolean>>(null);
    const [isReferenceActive, setIsReferenceActive] = useState<boolean>(
        isReferenceOnly || isCJReferenceInArgumentValue(defaultValue),
    );

    const { Component } = formConfig;

    const staticUpdateHandler = (data) => {
        // forms report their validation state through the same callback
        if (!isNil(data?.isValid) && isBoolean(data.isValid)) {
            setIsFormValid(data.isValid);
            return;
        }
        setValue(formConfig.mapToApiModel(data, CJArgumentValuesTypes.StaticInArgument, option));
    };

    const referenceChangeHandler = ({ parentValue, value: propertyName, logicFunction }) => {
        setValue(
            formConfig.mapToApiModel(
                { blockId: parentValue, propertyName, logicFunction },
                CJArgumentValuesTypes.ReferenceInArgument,
                option,
            ),
        );
    };

    const referenceClearHandler = () => {
        setValue(null);
    };

    const referenceValidationChangeHandler = (isValid: boolean) => {
        setIsFormValid(isValid);
    };

    const isRequired = option?.Required === true;

    const referenceActiveClickHandler = () => {
        setValue(null);
        setIsFormValid(null);
        setIsReferenceActive((prev) => !prev);
    };

    useEffect(() => {
        const hasValue = isReferenceActive
            ? isObject(value) && !isNil((value as AnyObject).Block ?? (value as AnyObject).Arguments)
            : !isNil(value);
        const isValid = (!isRequired || hasValue) && isFormValid !== false;
        getUpdate({ value: hasValue ? value : null, isValid });
        // DEPS: [getUpdate, isRequired]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, isFormValid, isReferenceActive]);

    const renderEditor = () =>
        isReferenceActive ? (
            <CJCascadeDropdown
                data={referenceProperties}
                value={formulaToReferenceMapToUIModel(value)}
                elements={elements ?? []}
                option={option}
                node={node}
                onChange={referenceChangeHandler}
                onValidationChange={referenceValidationChangeHandler}
                isEmptyValueValid={!isRequired}
                label={hasSection ? undefined : label}
                onClear={isRequired ? undefined : referenceClearHandler}
            />
        ) : (
            <Suspense fallback={<BusyLoader isBusy type="spinner" spinnerSize="medium" />}>
                <Component
                    getUpdate={staticUpdateHandler}
                    option={option}
                    defaultValue={formConfig.mapToUIModel(value)}
                    node={node}
                    elements={elements}
                />
            </Suspense>
        );

    if (!hasSection) {
        return <div className={classNames(styles.referenceValue, contentClassName)}>{renderEditor()}</div>;
    }

    return (
        <SectionWithTitle
            className={styles.referenceValue}
            contentClassName={contentClassName}
            title={label}
            titleContent={
                isReferenceOnly ? null : (
                    <Button
                        className={styles.referenceValueSwitch}
                        icon={isReferenceActive ? 'bc-icon-unlink' : 'bc-icon-link'}
                        appearance="minimal"
                        onClick={referenceActiveClickHandler}
                    >
                        {isReferenceActive ? t(l.PrimitiveValue) : t(l.Reference)}
                    </Button>
                )
            }
        >
            {renderEditor()}
        </SectionWithTitle>
    );
};

CustomerJourneyVisualizationReferenceValue.displayName = 'CustomerJourneyVisualizationReferenceValue';

export default memo(CustomerJourneyVisualizationReferenceValue);
